import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, ArrowRight } from "lucide-react";
import { site, addressLine, enabledServices, areas } from "@/config/site";
import { Logo } from "@/components/Logo";
import { trackEvent } from "@/lib/analytics";

export function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="bg-slate-950 text-slate-300" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <Logo variant="light" />
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-slate-400">
              Professional painting for homes and businesses in Delta, Ladner, Tsawwassen and Surrey — careful
              preparation, clean lines and finishes that last.
            </p>
            <p className="mt-4 text-xs leading-relaxed text-slate-500">
              Fully insured · {site.yearsExperience} years of experience
            </p>
          </div>

          <nav aria-label="Services">
            <h2 className="text-sm font-semibold uppercase tracking-[0.2em] text-ember">Services</h2>
            <ul className="mt-5 space-y-2.5">
              {enabledServices.slice(0, 8).map((s) => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} data-testid={`footer-service-${s.slug}`} className="text-sm text-slate-400 transition-colors duration-200 hover:text-ember">
                    {s.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/services" data-testid="footer-service-all" className="text-sm font-semibold text-slate-200 transition-colors duration-200 hover:text-ember">
                  All services
                </Link>
              </li>
            </ul>
          </nav>

          <nav aria-label="Company">
            <h2 className="text-sm font-semibold uppercase tracking-[0.2em] text-ember">Company</h2>
            <ul className="mt-5 space-y-2.5">
              {[
                ["/about", "About Us"],
                ["/projects", "Our Projects"],
                ["/process", "Our Process"],
                ["/pricing", "Pricing & Estimates"],
                ["/colours", "Colour Palette Tool"],
                ["/blog", "Painting Advice"],
                ["/faqs", "FAQs"],
                ["/contact", "Contact Us"],
              ].map(([to, label]) => (
                <li key={to}>
                  <Link to={to} data-testid={`footer-nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="text-sm text-slate-400 transition-colors duration-200 hover:text-ember">
                    {label}
                  </Link>
                </li>
              ))}
              {areas.map((a) => (
                <li key={a.slug}>
                  <Link to={`/service-areas/${a.slug}`} data-testid={`footer-area-${a.slug}`} className="text-sm text-slate-400 transition-colors duration-200 hover:text-ember">
                    Painters in {a.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div>
            <h2 className="text-sm font-semibold uppercase tracking-[0.2em] text-ember">Contact</h2>
            <ul className="mt-5 space-y-4 text-sm">
              <li>
                <a href={site.phoneHref} data-testid="footer-phone" onClick={() => trackEvent("phone_click", { location: "footer" })} className="flex items-start gap-3 text-slate-300 transition-colors duration-200 hover:text-ember">
                  <Phone className="mt-0.5 h-4 w-4 shrink-0 text-ember" aria-hidden="true" />
                  {site.phone}
                </a>
              </li>
              <li>
                <a href={site.emailHref} data-testid="footer-email" onClick={() => trackEvent("email_click", { location: "footer" })} className="flex items-start gap-3 break-all text-slate-300 transition-colors duration-200 hover:text-ember">
                  <Mail className="mt-0.5 h-4 w-4 shrink-0 text-ember" aria-hidden="true" />
                  {site.email}
                </a>
              </li>
              <li className="flex items-start gap-3 text-slate-400">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-ember" aria-hidden="true" />
                {site.showAddress ? addressLine : "Serving Delta, Ladner, Tsawwassen & Surrey, BC"}
              </li>
              <li className="flex items-start gap-3 text-slate-400">
                <Clock className="mt-0.5 h-4 w-4 shrink-0 text-ember" aria-hidden="true" />
                {site.hours}
              </li>
            </ul>
            {site.socials.length > 0 && (
              <ul className="mt-5 flex gap-3" aria-label="Social media">
                {site.socials.map((s) => (
                  <li key={s.label}>
                    <a href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.label} className="text-slate-400 hover:text-ember">
                      {s.label}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="mt-12 rounded-2xl border border-white/10 bg-white/5 p-6 sm:flex sm:items-center sm:justify-between">
          <p className="text-sm text-slate-300">Planning a project in Delta or nearby? Let's talk it through.</p>
          <Link
            to="/estimate"
            data-testid="footer-estimate-btn"
            className="mt-4 inline-flex min-h-[44px] items-center gap-2 rounded-full bg-ember px-5 py-2.5 text-sm font-semibold text-white transition-[background-color] duration-300 hover:bg-ember-dark sm:mt-0"
          >
            Get a Free Estimate <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>

        <div className="mt-10 flex flex-col gap-3 border-t border-white/10 pt-6 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between">
          <p>© {year} {site.name}. All rights reserved.</p>
          <div className="flex gap-5">
            <Link to="/privacy" data-testid="footer-privacy" className="transition-colors duration-200 hover:text-ember">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms" className="transition-colors duration-200 hover:text-ember">Terms of Use</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
